import { useEffect, useState } from 'react';
import type { StreamDebugOverlaySnapshot } from '@playcastdotio/PeerCore';
import {
  classifyImplementationName,
  formatStreamDebugOverlayLine,
} from '@playcastdotio/PeerCore';
import { getClientPeerManager, getPeer } from '../../api/modules/peer';

const EMPTY_SNAPSHOT: StreamDebugOverlaySnapshot = {
  codecKind: 'unknown',
  framesPerSecond: 0,
  frameWidth: 0,
  frameHeight: 0,
};

const STATS_POLL_MS = 1000;

type ConnectionHolder = {
  getRtcPeerConnection?: () => RTCPeerConnection | null;
};

type SessionWithConnection = {
  connection?: ConnectionHolder | null;
};

type ManagerWithSession = {
  session?: SessionWithConnection;
};

/** Resolve guest RTCPeerConnection from ClientPeerManager / GuestPeerSession internals. */
export function resolveGuestRtcPeerConnection(holder: unknown): RTCPeerConnection | null {
  const asManager = holder as ManagerWithSession | null | undefined;
  const session = asManager?.session ?? (holder as SessionWithConnection | null | undefined);
  const conn = session?.connection;
  if (!conn || typeof conn.getRtcPeerConnection !== 'function') return null;
  try {
    return conn.getRtcPeerConnection() ?? null;
  } catch {
    return null;
  }
}

/** Build overlay snapshot from an inbound-rtp video getStats report. */
export function snapshotFromInboundStatsReport(report: RTCStatsReport): StreamDebugOverlaySnapshot {
  let framesPerSecond = 0;
  let frameWidth = 0;
  let frameHeight = 0;
  let decoderImplementation: string | undefined;

  report.forEach((stat) => {
    if (stat.type !== 'inbound-rtp') return;
    const s = stat as RTCInboundRtpStreamStats & {
      kind?: string;
      decoderImplementation?: string;
      framesPerSecond?: number;
      frameWidth?: number;
      frameHeight?: number;
    };
    if (s.kind !== 'video') return;
    if (typeof s.framesPerSecond === 'number') {
      framesPerSecond = Math.max(framesPerSecond, s.framesPerSecond);
    }
    if (typeof s.frameWidth === 'number') frameWidth = Math.max(frameWidth, s.frameWidth);
    if (typeof s.frameHeight === 'number') frameHeight = Math.max(frameHeight, s.frameHeight);
    if (typeof s.decoderImplementation === 'string' && s.decoderImplementation) {
      decoderImplementation = s.decoderImplementation;
    }
  });

  return {
    codecKind: classifyImplementationName(decoderImplementation),
    framesPerSecond,
    frameWidth,
    frameHeight,
    implementation: decoderImplementation,
  };
}

/** Fallback when RTCPeerConnection is unavailable (legacy peer statistics store). */
function snapshotFromLegacyPeerStatistics(): StreamDebugOverlaySnapshot | null {
  const peer = getPeer();
  const stats = peer?.statistics?.currentState;
  const inbound = stats?.rtp?.inbound;
  if (!inbound || inbound.length === 0) return null;

  let framesPerSecond = 0;
  let frameWidth = 0;
  let frameHeight = 0;
  for (const entry of inbound) {
    if ((entry as { kind?: string }).kind && (entry as { kind?: string }).kind !== 'video') continue;
    framesPerSecond = Math.max(framesPerSecond, entry.framesPerSecond ?? 0);
    frameWidth = Math.max(frameWidth, entry.frameWidth ?? 0);
    frameHeight = Math.max(frameHeight, entry.frameHeight ?? 0);
  }

  if (framesPerSecond === 0 && frameWidth === 0 && frameHeight === 0) return null;

  return {
    codecKind: 'unknown',
    framesPerSecond,
    frameWidth,
    frameHeight,
  };
}

export type StreamDebugOverlayProps = {
  snapshot: StreamDebugOverlaySnapshot;
};

/**
 * Presentational guest stream debug line (fixed over player).
 * Non-interactive — does not capture pointer events.
 */
export function StreamDebugOverlay({ snapshot }: StreamDebugOverlayProps) {
  return (
    <div
      className='pointer-events-none fixed left-3 top-3 z-[2000] rounded bg-black/60 px-2 py-1 font-mono text-xs text-emerald-300'
      data-testid='stream-debug-overlay'
      aria-hidden='true'
    >
      {formatStreamDebugOverlayLine(snapshot)}
    </div>
  );
}

export type GuestStreamDebugOverlayProps = {
  /** Optional injector for tests; defaults to ClientPeerManager / legacy peer. */
  getRtcPeerConnection?: () => RTCPeerConnection | null;
};

/**
 * Guest debug overlay — polls getStats ≤1s only while mounted.
 * Mount only when `?debug` / isDebugMode is active.
 */
export function GuestStreamDebugOverlay({ getRtcPeerConnection }: GuestStreamDebugOverlayProps = {}) {
  const snapshot = useGuestStreamDebugSnapshot(getRtcPeerConnection);
  return <StreamDebugOverlay snapshot={snapshot} />;
}

/** The guest's inbound stream snapshot, polled from getStats once a second while mounted. */
export function useGuestStreamDebugSnapshot(
  getRtcPeerConnection?: () => RTCPeerConnection | null,
): StreamDebugOverlaySnapshot {
  const [snapshot, setSnapshot] = useState<StreamDebugOverlaySnapshot>(EMPTY_SNAPSHOT);

  useEffect(() => {
    let cancelled = false;

    const resolvePc = (): RTCPeerConnection | null => {
      if (getRtcPeerConnection) return getRtcPeerConnection();
      return resolveGuestRtcPeerConnection(getClientPeerManager());
    };

    const poll = async () => {
      const pc = resolvePc();
      if (pc && typeof pc.getStats === 'function') {
        try {
          const report = await pc.getStats();
          if (!cancelled) setSnapshot(snapshotFromInboundStatsReport(report));
          return;
        } catch {
          // fall through to legacy statistics
        }
      }

      const legacy = snapshotFromLegacyPeerStatistics();
      if (!cancelled) setSnapshot(legacy ?? EMPTY_SNAPSHOT);
    };

    void poll();
    const interval = setInterval(() => {
      void poll();
    }, STATS_POLL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [getRtcPeerConnection]);

  return snapshot;
}
